// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { notifications } from '@mantine/notifications';
import { getReferenceString, normalizeErrorString } from '@medplum/core';
import type { Communication } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useSubscription } from '@medplum/react-hooks';
import { isRead } from '@polarissleep/shared';
import { IconAlertTriangle } from '@tabler/icons-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface UseUnreadThreadCountOptions {
  /** Reference of the current user (e.g. "Practitioner/abc"). When undefined the hook returns 0. */
  recipientRef: string | undefined;
}

/**
 * Count of distinct parent threads in the "Unread" bucket — threads where the
 * last child message is not sent by the current user AND has no
 * notification-readAt extension. Subscribes to inbound Communications addressed
 * to the recipient so the count updates in real time as replies arrive.
 */
export function useUnreadThreadCount({ recipientRef }: UseUnreadThreadCountOptions): number {
  const medplum = useMedplum();
  const [count, setCount] = useState(0);

  const recompute = useCallback(async (): Promise<void> => {
    if (!recipientRef) {
      setCount(0);
      return;
    }

    const parents = await medplum.searchResources(
      'Communication',
      [
        ['part-of:missing', 'true'],
        ['_has:Communication:part-of:_id:not', 'null'],
        ['identifier:not', 'ai-message-topic'],
        ['status', 'in-progress'],
        ['recipient', recipientRef],
        ['_count', '50'],
      ],
      { cache: 'no-cache' }
    );

    if (parents.length === 0) {
      setCount(0);
      return;
    }

    const queryParts = parents.map((parent) => {
      const safeId = parent.id?.replace(/-/g, '') || '';
      const alias = `thread_${safeId}`;
      const ref = getReferenceString(parent);
      return `
          ${alias}: CommunicationList(
            part_of: "${ref}"
            _sort: "-sent"
            _count: 1
          ) {
            sender { reference }
            extension { url }
          }
        `;
    });

    const response = await medplum.graphql(`query { ${queryParts.join('\n')} }`);

    let unread = 0;
    for (const parent of parents) {
      const safeId = parent.id?.replace(/-/g, '') || '';
      const alias = `thread_${safeId}`;
      const list = response.data[alias] as Communication[] | undefined;
      const last = list && list.length > 0 ? list[0] : undefined;
      if (!last) {
        continue;
      }
      if (last.sender?.reference === recipientRef) {
        continue;
      }
      if (!isRead(last)) {
        unread += 1;
      }
    }
    setCount(unread);
  }, [medplum, recipientRef]);

  useEffect(() => {
    recompute().catch(() => setCount(0));
  }, [recompute]);

  const warnedRef = useRef(false);
  useSubscription(
    recipientRef ? `Communication?recipient=${recipientRef}` : undefined,
    () => {
      recompute().catch(() => undefined);
    },
    {
      onError: (err) => {
        if (warnedRef.current) {
          return;
        }
        warnedRef.current = true;
        notifications.show({
          color: 'yellow',
          icon: React.createElement(IconAlertTriangle),
          title: 'Inbox live updates unavailable',
          message: `Your account is missing access to message subscriptions, so new messages won't update in real time. Refresh to see new messages. Contact support if this persists. (${normalizeErrorString(err)})`,
          autoClose: 8000,
        });
      },
    }
  );

  return count;
}
